import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionPrimary,
  PageHeaderActions,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  ListBrowseToolbar,
  SegmentedFilterChip,
  useThreads,
} from '@yeshuman/sdk'
import { useState, useEffect, useRef } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { Eye, Plus, X, MessageSquare } from 'lucide-react'
import { Observation, useObservations, useCreateObservation, useUpdateObservation, useDeleteObservation, ObservationFilters, CreateObservationData, UpdateObservationData } from '../hooks/use-observations'
import { ObservationCard } from './observation-card'
import { ObservationFormDialog } from './observation-form-dialog'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'

export function ObservationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Read thread parameter from URL
  const threadIdFromUrl = searchParams.get('thread')
  
  const [filters, setFilters] = useState<ObservationFilters>({})
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingObservation, setEditingObservation] = useState<Observation | null>(null)
  const [deletingObservationId, setDeletingObservationId] = useState<string | null>(null)
  
  // Track previous observations for agent update animation
  const [highlightedObservations, setHighlightedObservations] = useState<Set<string>>(new Set())
  const previousObservationsRef = useRef<Observation[]>([])

  // Sync thread_id from URL to filters
  useEffect(() => {
    if (threadIdFromUrl) {
      setFilters(prev => ({ ...prev, thread_id: threadIdFromUrl }))
    } else {
      // Remove thread_id from filters if not in URL
      setFilters(prev => {
        const { thread_id, ...rest } = prev
        return rest
      })
    }
  }, [threadIdFromUrl])

  // Fetch threads to get thread name for display
  const { threads } = useThreads()
  const currentThread = threads?.find(t => t.id === threadIdFromUrl)

  const { observations, isLoading, error, refetch } = useObservations(filters)
  const createObservation = useCreateObservation()
  const updateObservation = useUpdateObservation()
  const deleteObservation = useDeleteObservation()

  // Detect agent updates and highlight changed observations
  useEffect(() => {
    if (observations && previousObservationsRef.current.length > 0) {
      const changedIds = new Set<string>()
      
      // Find new observations
      observations.forEach((observation) => {
        const prev = previousObservationsRef.current.find((o) => o.id === observation.id)
        if (!prev) {
          changedIds.add(observation.id)
        } else {
          // Check if observation was updated (compare JSON strings)
          if (JSON.stringify(prev) !== JSON.stringify(observation)) {
            changedIds.add(observation.id)
          }
        }
      })
      
      if (changedIds.size > 0) {
        setHighlightedObservations(changedIds)
        // Fade out highlights after 3 seconds
        setTimeout(() => {
          setHighlightedObservations(new Set())
        }, 3000)
      }
    }
    
    // Update ref (doesn't trigger re-render)
    if (observations) {
      previousObservationsRef.current = observations
    }
  }, [observations])

  const handleCreate = async (data: CreateObservationData) => {
    try {
      await createObservation.mutateAsync(data)
      toast.success('Observation recorded successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to create observation')
    }
  }

  const handleUpdate = async (data: UpdateObservationData) => {
    try {
      await updateObservation.mutateAsync(data)
      toast.success('Observation updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update observation')
    }
  }

  const handleDelete = async (observationId: string) => {
    try {
      await deleteObservation.mutateAsync(observationId)
      toast.success('Observation deleted successfully')
      setDeletingObservationId(null)
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete observation')
    }
  }

  const handleEdit = (observation: Observation) => {
    setEditingObservation(observation)
    setIsFormOpen(true)
  }

  const handleAdd = () => {
    setEditingObservation(null)
    setIsFormOpen(true)
  }

  const handleFormClose = () => {
    setIsFormOpen(false)
    setEditingObservation(null)
  }

  const handleClearThreadFilter = () => {
    const params = new URLSearchParams(searchParams)
    params.delete('thread')
    setSearchParams(params)
  }

  // Group observations by date
  const groupObservationsByDate = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const weekAgo = new Date(today)
    weekAgo.setDate(weekAgo.getDate() - 7)

    const groups = {
      today: [] as Observation[],
      yesterday: [] as Observation[],
      thisWeek: [] as Observation[],
      older: [] as Observation[],
    }

    observations.forEach((observation) => {
      const observationDate = new Date(observation.created_at)
      observationDate.setHours(0, 0, 0, 0)

      if (observationDate.getTime() === today.getTime()) {
        groups.today.push(observation)
      } else if (observationDate.getTime() === yesterday.getTime()) {
        groups.yesterday.push(observation)
      } else if (observationDate >= weekAgo) {
        groups.thisWeek.push(observation)
      } else {
        groups.older.push(observation)
      }
    })

    return groups
  }

  const groupedObservations = groupObservationsByDate()
  const { title: baseTitle, description: baseDesc } = getPageHeader('/observations', 'Observations', "Record generalized statements about how you're feeling or your health state.")

  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={
            <>
              {baseTitle}
              {threadIdFromUrl && (
                <Badge variant="outline" className="ml-2">
                  <MessageSquare className="h-3.5 w-3.5 mr-1" />
                  Thread Filter
                </Badge>
              )}
            </>
          }
          description={
            threadIdFromUrl
              ? currentThread?.subject
                ? `Observations from "${currentThread.subject}"`
                : 'Observations from thread'
              : baseDesc
          }
          icon={<Eye className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={handleAdd}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Record Observation
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        {/* Filters */}
        <div className="space-y-2">
          <ListBrowseToolbar
            searchId="observations-search"
            searchPlaceholder="Search observations…"
            searchValue={filters.search || ''}
            onSearchChange={(value) => setFilters({ ...filters, search: value || undefined })}
          />
          {threadIdFromUrl ? (
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedFilterChip
                tint="muted"
                label="Thread"
                value={currentThread?.subject || `${threadIdFromUrl.slice(0, 8)}…`}
                onRemove={handleClearThreadFilter}
                removeAriaLabel="Clear thread filter"
              />
            </div>
          ) : null}
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load observations: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading observations" /> : null}

        {/* Observations List */}
        {!isLoading && !error && (
          <div className="space-y-6">
            {observations.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Eye className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    {threadIdFromUrl ? (
                      <>
                        <h3 className="text-lg font-semibold mb-2">No observations found for this thread</h3>
                        <p className="text-muted-foreground mb-4">
                          {currentThread?.subject 
                            ? `No observations have been recorded in "${currentThread.subject}" yet.`
                            : 'No observations have been recorded in this thread yet.'}
                        </p>
                        <div className="flex gap-2 justify-center">
                          <Button variant="outline" onClick={handleClearThreadFilter}>
                            <X className="h-4 w-4 mr-2" />
                            Clear Thread Filter
                          </Button>
                          <Button onClick={handleAdd}>
                            <Plus className="h-4 w-4 mr-2" />
                            Record Observation
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="text-lg font-semibold mb-2">No observations recorded yet</h3>
                        <p className="text-muted-foreground mb-4">
                          Start tracking your health observations by recording your first observation.
                        </p>
                        <Button onClick={handleAdd}>
                          <Plus className="h-4 w-4 mr-2" />
                          Record Your First Observation
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {groupedObservations.today.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Today</h2>
                    <div className="grid gap-4">
                      {groupedObservations.today.map((observation) => (
                        <ObservationCard
                          key={observation.id}
                          observation={observation}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingObservationId(id)}
                          isHighlighted={highlightedObservations.has(observation.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedObservations.yesterday.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Yesterday</h2>
                    <div className="grid gap-4">
                      {groupedObservations.yesterday.map((observation) => (
                        <ObservationCard
                          key={observation.id}
                          observation={observation}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingObservationId(id)}
                          isHighlighted={highlightedObservations.has(observation.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedObservations.thisWeek.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">This Week</h2>
                    <div className="grid gap-4">
                      {groupedObservations.thisWeek.map((observation) => (
                        <ObservationCard
                          key={observation.id}
                          observation={observation}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingObservationId(id)}
                          isHighlighted={highlightedObservations.has(observation.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {groupedObservations.older.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="text-xl font-semibold">Older</h2>
                    <div className="grid gap-4">
                      {groupedObservations.older.map((observation) => (
                        <ObservationCard
                          key={observation.id}
                          observation={observation}
                          onEdit={handleEdit}
                          onDelete={(id) => setDeletingObservationId(id)}
                          isHighlighted={highlightedObservations.has(observation.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Create/Edit Form Dialog */}
        <ObservationFormDialog
          open={isFormOpen}
          onOpenChange={handleFormClose}
          observation={editingObservation}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deletingObservationId !== null} onOpenChange={(open) => !open && setDeletingObservationId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete observation</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this observation? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingObservationId && handleDelete(deletingObservationId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}

